/**
 * Occupations List Page
 * Main page for listing and managing occupations
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Warning as RiskIcon,
  Factory as SourceIcon,
  Science as ExamIcon,
  FileDownload as ExportIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  Restaurant as FoodHandlingIcon,
  ElectricBolt as ElectricalIcon,
  Elevator as PemtIcon,
  Compress as ConfinedSpaceIcon,
  Height as HeightWorkIcon,
  LocalFireDepartment as HotWorkIcon,
  Forklift as ForkliftIcon,
  Agriculture as SelfPropelledMachineIcon,
  DirectionsCar as VehicleDrivingIcon,
  Masks as RespiratoryProtectionIcon,
  Warning as IonizingRadiationIcon,
  FireExtinguisher as EmergencyBrigadeIcon,
  ScubaDiving as HyperbaricWorkIcon,
  Park as ChainsawIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

// Components
import { OccupationFilters } from './components';
import {
  PageShell,
  DataTable,
  type DataTableColumn,
  AlertBadge,
  generateOccupationAlerts,
  RouteActionButton,
} from '../../components';
import { pode } from '../../authorization/pode';

// Services and types
import {
  occupationService,
  Occupation,
  OccupationFilters as OccupationFiltersType,
  SpecialActivityType,
  SPECIAL_ACTIVITIES,
  OccupationSpecialActivity,
} from '../../services/occupationService';
import { companyService } from '../../services/companyService';
import { departmentService } from '../../services/departmentService';
import { getApiErrorMessage } from '../../types/common';

// Hooks
import { useAcao, useControle } from '../../authorization/useVeredito';
import { useOccupationExport } from '../../hooks/useOccupationExport';

interface Company {
  id: number;
  tradeName: string;
  legalName: string;
}

interface Department {
  id: number;
  name: string;
  companyId: number;
}

interface OccupationFiltersData {
  searchTerm: string;
  companyId: string;
  departmentId: string;
  specialActivities: string[];
}

// Map special activity type to MUI icon and color (same pattern as DepartmentTable safety icons)
const ACTIVITY_ICON_MAP: Record<string, { icon: typeof HeightWorkIcon; color: string }> = {
  [SpecialActivityType.HEIGHT_WORK]: { icon: HeightWorkIcon, color: 'purple' },
  [SpecialActivityType.CONFINED_SPACE]: { icon: ConfinedSpaceIcon, color: 'red' },
  [SpecialActivityType.ELECTRICAL]: { icon: ElectricalIcon, color: 'yellow' },
  [SpecialActivityType.FORKLIFT]: { icon: ForkliftIcon, color: 'green' },
  [SpecialActivityType.FOOD_HANDLING]: { icon: FoodHandlingIcon, color: 'orange' },
  [SpecialActivityType.PEMT]: { icon: PemtIcon, color: 'blue' },
  [SpecialActivityType.HOT_WORK]: { icon: HotWorkIcon, color: 'red' },
  [SpecialActivityType.SELF_PROPELLED_MACHINE]: { icon: SelfPropelledMachineIcon, color: 'gray' },
  [SpecialActivityType.VEHICLE_DRIVING]: { icon: VehicleDrivingIcon, color: 'blue' },
  [SpecialActivityType.RESPIRATORY_PROTECTION]: { icon: RespiratoryProtectionIcon, color: 'orange' },
  [SpecialActivityType.IONIZING_RADIATION]: { icon: IonizingRadiationIcon, color: 'yellow' },
  [SpecialActivityType.EMERGENCY_BRIGADE]: { icon: EmergencyBrigadeIcon, color: 'red' },
  [SpecialActivityType.HYPERBARIC_WORK]: { icon: HyperbaricWorkIcon, color: 'blue' },
  [SpecialActivityType.CHAINSAW_OPERATION]: { icon: ChainsawIcon, color: 'green' },
};

// Get activity icons array for an occupation
const getActivityIcons = (activities?: OccupationSpecialActivity[]) => {
  if (!activities || activities.length === 0) return [];

  return activities.map((item) => {
    const config = SPECIAL_ACTIVITIES.find(a => a.value === item.activity);
    const iconConfig = ACTIVITY_ICON_MAP[item.activity];
    return {
      icon: iconConfig?.icon || WarningIcon,
      label: config?.label || item.activity,
      color: iconConfig?.color || 'gray',
      key: item.id,
    };
  });
};

// Get alerts for an occupation
const getOccupationAlerts = (occupation: Occupation) => {
  if (!occupation._counts) return [];
  return generateOccupationAlerts({
    employeeCount: occupation._counts.employees,
    riskApplicationCount: occupation._counts.riskApplications,
    sourceApplicationCount: occupation._counts.sourceApplications,
    examApplicationCount: occupation._counts.examApplications,
  });
};

const columns: DataTableColumn<Occupation>[] = [
  {
    id: 'name',
    label: 'Nome do Cargo',
    primary: true,
    render: (occupation) => (
      <>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2" fontWeight={500}>
            {occupation.name}
          </Typography>
          <AlertBadge alerts={getOccupationAlerts(occupation)} />
        </Box>
        {occupation.role && (
          <Typography variant="caption" color="text.secondary">
            {occupation.role}
          </Typography>
        )}
      </>
    ),
  },
  {
    id: 'cboCode',
    label: 'CBO',
    render: (occupation) => (
      <Typography variant="body2">
        {occupation.cboCode ?? '-'}
      </Typography>
    ),
  },
  {
    id: 'department',
    label: 'Departamento',
    render: (occupation) => (
      <Typography variant="body2">
        {occupation.department?.name || '-'}
      </Typography>
    ),
  },
  {
    id: 'company',
    label: 'Empresa',
    render: (occupation) => (
      <Typography variant="body2">
        {occupation.company?.tradeName || occupation.company?.legalName || '-'}
      </Typography>
    ),
  },
  {
    id: 'specialActivities',
    label: 'Atividades Especiais',
    render: (occupation) => {
      const icons = getActivityIcons(occupation.specialActivities);
      if (icons.length === 0) return '-';
      return (
        <Box display="flex" flexWrap="wrap" gap={0.5} alignItems="center">
          {icons.slice(0, 3).map((activity) => (
            <Tooltip key={activity.key} title={activity.label}>
              <activity.icon
                fontSize="small"
                sx={{ color: activity.color }}
              />
            </Tooltip>
          ))}
          {icons.length > 3 && (
            <Typography variant="caption" color="text.secondary">
              +{icons.length - 3}
            </Typography>
          )}
        </Box>
      );
    },
  },
];

const OccupationsListPage: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const editar = useControle('/occupations/:id/edit', pode('occupation.update'), 'Editar');
  const excluir = useAcao('occupation.delete', 'Excluir');
  const { exporting, exportOccupations } = useOccupationExport();

  // State
  const [occupations, setOccupations] = useState<Occupation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [currentFilters, setCurrentFilters] = useState<Partial<OccupationFiltersType>>({});
  const [searchTerm, setSearchTerm] = useState('');

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [occupationToDelete, setOccupationToDelete] = useState<Occupation | null>(null);

  // Companies and departments for filters
  const [companies, setCompanies] = useState<Company[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);

  // Load companies and departments for filters
  useEffect(() => {
    const loadFilterData = async () => {
      try {
        const [companiesRes, departmentsRes] = await Promise.all([
          companyService.getCompanies({ page: 1, limit: 1000 }),
          departmentService.getDepartments({ page: 1, limit: 1000 })
        ]);

        setCompanies(companiesRes.data.map(c => ({
          id: Number(c.id),
          tradeName: c.tradeName || c.legalName,
          legalName: c.legalName
        })));

        setDepartments(departmentsRes.data.map(d => ({
          id: Number(d.id),
          name: d.name,
          companyId: Number(d.companyId)
        })));
      } catch (error) {
        console.error('Error loading filter data:', error);
      }
    };

    loadFilterData();
  }, []);

  // Load occupations from API
  const loadOccupations = useCallback(async (
    pageNumber: number,
    limit: number,
    search: string,
    filters: Partial<OccupationFiltersType>
  ) => {
    setLoading(true);
    setError(null);

    try {
      const response = await occupationService.getOccupations({
        page: pageNumber,
        limit,
        search,
        filters
      });

      setOccupations(response.data);
      setTotalCount(response.pagination.total);
    } catch (err) {
      console.error('Error loading occupations:', err);
      setError(getApiErrorMessage(err, 'Erro ao carregar cargos'));
      enqueueSnackbar('Erro ao carregar cargos', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  // Apply filters
  const applyFilters = (filters: OccupationFiltersData) => {
    const apiFilters: Partial<OccupationFiltersType> = {};

    if (filters.companyId) {
      apiFilters.companyId = Number(filters.companyId);
    }

    if (filters.departmentId) {
      apiFilters.departmentId = Number(filters.departmentId);
    }

    if (filters.specialActivities.length > 0) {
      apiFilters.specialActivities = filters.specialActivities as SpecialActivityType[];
    }

    setCurrentFilters(apiFilters);
    setSearchTerm(filters.searchTerm);
    setPage(1);

    loadOccupations(1, rowsPerPage, filters.searchTerm, apiFilters);
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setPage(newPage + 1);
    loadOccupations(newPage + 1, rowsPerPage, searchTerm, currentFilters);
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
    loadOccupations(1, newRowsPerPage, searchTerm, currentFilters);
  };

  // Handle edit occupation
  const handleEditOccupation = (occupation: Occupation) => {
    navigate(`/occupations/${occupation.id}/edit`);
  };

  // Handle delete occupation
  const handleDeleteOccupation = async (occupation: Occupation) => {
    try {
      await occupationService.deleteOccupation(occupation.id);
      enqueueSnackbar('Cargo excluído com sucesso', { variant: 'success' });
      loadOccupations(page, rowsPerPage, searchTerm, currentFilters);
    } catch (err) {
      console.error('Error deleting occupation:', err);
      enqueueSnackbar(getApiErrorMessage(err, 'Erro ao excluir cargo'), { variant: 'error' });
    }
  };

  // Handle delete click
  const handleDeleteClick = (occupation: Occupation) => {
    setOccupationToDelete(occupation);
    setDeleteDialogOpen(true);
  };

  // Confirm delete
  const handleDeleteConfirm = () => {
    if (occupationToDelete) {
      handleDeleteOccupation(occupationToDelete);
    }
    setDeleteDialogOpen(false);
    setOccupationToDelete(null);
  };

  // Cancel delete
  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setOccupationToDelete(null);
  };

  // Load data on mount
  useEffect(() => {
    loadOccupations(1, 10, '', {});
  }, [loadOccupations]);

  // Convert for table display (MUI Table uses 0-based pagination)
  const displayPage = page - 1;

  const filtersActive = searchTerm.trim() !== '' || Object.keys(currentFilters).length > 0;

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Cargos' },
      ]}
      title="Cargos"
      actions={
        <>
          <RouteActionButton
            para="/risk-applications"
            exige={pode('risk_application.read', { perspectiva: 'plataforma' })}
            variant="outlined"
            color="warning"
            size="small"
            startIcon={<RiskIcon />}
          >
            Aplicar Riscos
          </RouteActionButton>
          <RouteActionButton
            para="/source-applications"
            exige={pode('source_application.read', { perspectiva: 'plataforma' })}
            variant="outlined"
            color="info"
            size="small"
            startIcon={<SourceIcon />}
          >
            Aplicar Fontes
          </RouteActionButton>
          <RouteActionButton
            para="/exam-applications"
            exige={pode('exam_application.read', { perspectiva: 'plataforma' })}
            variant="outlined"
            color="success"
            size="small"
            startIcon={<ExamIcon />}
          >
            Aplicar Exames
          </RouteActionButton>
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={() => exportOccupations(currentFilters)}
            disabled={loading || exporting}
          >
            Exportar
          </Button>
          <RouteActionButton
            para="/occupations/create"
            exige={pode('occupation.create')}
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
          >
            Novo Cargo
          </RouteActionButton>
        </>
      }
    >
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Box sx={{ mb: 3 }}>
        <OccupationFilters
          onFilter={applyFilters}
          companies={companies}
          departments={departments}
          loading={loading || exporting}
          total={totalCount}
        />
      </Box>

      {/* Occupations Table */}
      <DataTable
        columns={columns}
        rows={occupations}
        getRowId={(occupation) => occupation.id}
        loading={loading}
        emptyState={{
          title: 'Nenhum cargo cadastrado ainda',
          description:
            'Cargos descrevem a função de cada funcionário e definem os exames exigidos no PCMSO.',
          prereqChain: 'occupations',
          action: {
            label: 'Cadastrar cargo',
            node: 'occupations',
            deniedHint: 'Peça ao administrador da empresa para cadastrar um cargo.',
          },
        }}
        filtersActive={filtersActive}
        actions={[
          {
            icon: <EditIcon fontSize="small" />,
            onClick: handleEditOccupation,
            ...editar,
          },
          {
            icon: <DeleteIcon fontSize="small" />,
            color: 'error',
            onClick: handleDeleteClick,
            ...excluir,
          },
        ]}
        pagination={{
          page: displayPage,
          rowsPerPage,
          totalCount,
          onPageChange: handlePageChange,
          onRowsPerPageChange: handleRowsPerPageChange,
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="warning" />
            Confirmar Exclusão
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir o cargo <strong>{occupationToDelete?.name}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Esta ação não pode ser desfeita. Funcionários vinculados a este cargo impedirão a exclusão.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancelar</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default OccupationsListPage;
